import React, { useState, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, useSearchParams } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import AttractScreen from './components/AttractScreen';
import NameEntry from './components/NameEntry';
import BiodataEntry from './components/BiodataEntry';
import MOSSelection from './components/MOSSelection';
import PhotoCapture from './components/PhotoCapture';
import ProcessingScreen from './components/ProcessingScreen';
import RevealScreen from './components/RevealScreen';
import ResultPage from './components/ResultPage';
import Dashboard from './components/Dashboard';
import TimeoutModal from './components/TimeoutModal';
import { SessionProvider, useSession } from './contexts/SessionContext';
import { logMOSView } from './services/session';
import { AppScreen, UserData, BiologicalSex, MOSOption, GeneratedImages, AllMOSImages } from './types';
import useAudio from './hooks/useAudio';
import useSessionTimeout from './hooks/useSessionTimeout';

// Session timeout configuration
const TIMEOUT_MINUTES = 10;      // Show warning after 10 minutes of inactivity
const WARNING_SECONDS = 30;      // User has 30 seconds to respond

// Main experience component (kiosk)
function MainExperience() {
  const [currentScreen, setCurrentScreen] = useState<AppScreen>('attract');
  const [userData, setUserData] = useState<UserData>({});
  const [allMOSImages, setAllMOSImages] = useState<AllMOSImages | null>(null);
  const { playSound, startMusic, stopMusic } = useAudio();
  const { session, createSession, updateSession, uploadImages, resetSession } = useSession();

  // Ref to prevent multiple calls to handleProcessingComplete
  const processingCompleteCalledRef = useRef(false);

  // Ref to always have the latest allMOSImages (avoids stale closure in handleMOSSelection)
  const allMOSImagesRef = useRef(allMOSImages);
  allMOSImagesRef.current = allMOSImages;

  // Handle session timeout - restart to attract screen
  const handleSessionTimeout = () => {
    stopMusic();
    setUserData({});
    setAllMOSImages(null);
    resetSession();
    setCurrentScreen('attract');
  };

  // Session timeout - pause during processing and on attract screen
  const isTimeoutPaused = currentScreen === 'processing' || currentScreen === 'attract';

  const {
    showWarning,
    handleStayActive,
    handleWarningTimeout,
  } = useSessionTimeout({
    timeoutMinutes: TIMEOUT_MINUTES,
    warningSeconds: WARNING_SECONDS,
    isPaused: isTimeoutPaused,
    onTimeout: handleSessionTimeout,
  });

  // Navigation handlers
  const handleStart = () => {
    // Create session in background (don't block UI transition)
    createSession().then(success => {
      if (!success) {
        console.error('Failed to create session, continuing anyway');
      }
    });
    // Begin background music
    startMusic(0.15);
    setCurrentScreen('name-entry');
  };

  const handleNameEntry = (lastName: string) => {
    setUserData(prev => ({ ...prev, lastName }));
    // Update session in background (don't block UI transition)
    updateSession({ lastName });
    setCurrentScreen('biodata');
  };

  const handleSexSelection = (sex: BiologicalSex) => {
    setUserData(prev => ({ ...prev, sex }));
    // Update session in background (don't block UI transition)
    updateSession({ sex });
    setCurrentScreen('mos-selection');
  };

  const handleMOSSelection = useCallback(async (mos: MOSOption) => {
    setUserData(prev => ({ ...prev, selectedMOS: mos }));
    // Update session in background (don't block UI transition)
    updateSession({ selectedMOS: mos });
    // Stop music when leaving MOS selection
    stopMusic();

    // Read latest cache via ref to avoid stale closure
    const cache = allMOSImagesRef.current;
    const isInitial = !cache || Object.keys(cache).length === 0;

    // Log MOS view for analytics (fire and forget - don't block UI)
    if (session.sessionId) {
      logMOSView(session.sessionId, mos.id, mos.title, isInitial)
        .catch(err => console.error('Failed to log MOS view:', err));
    }

    // If we have pre-generated images for this MOS, jump straight to reveal
    if (cache && cache[mos.id]) {
      console.log('Using pre-generated images for MOS:', mos.id);
      const images = cache[mos.id];
      await preloadImages([images.portrait, images.field]);
      setUserData(prev => ({
        ...prev,
        selectedMOS: mos,
        generatedImages: images,
      }));
      setCurrentScreen('reveal');
      return;
    }

    // No cache yet - first pass must run photo capture + generation
    setCurrentScreen('photo-capture');
  }, [updateSession, stopMusic, session.sessionId]);

  const handlePhotoCapture = (imageData: string) => {
    setUserData(prev => ({ ...prev, capturedImage: imageData }));
    // Reset the processing complete guard for new processing
    processingCompleteCalledRef.current = false;
    // Update session in background (don't block UI transition)
    updateSession({ originalPhoto: imageData, status: 'photo_captured' });
    setCurrentScreen('processing');
  };

  // Back navigation handlers
  const handleBackToAttract = () => {
    stopMusic();
    setUserData({});
    setAllMOSImages(null);
    resetSession();
    setCurrentScreen('attract');
  };

  const handleBackToNameEntry = () => {
    setUserData(prev => ({ ...prev, lastName: undefined }));
    setCurrentScreen('name-entry');
  };

  const handleBackToBiodata = () => {
    setUserData(prev => ({ ...prev, sex: undefined }));
    setCurrentScreen('biodata');
  };

  const handleBackToMOS = () => {
    setUserData(prev => ({ ...prev, selectedMOS: undefined }));
    // Restart music when going back to MOS selection
    startMusic(0.15);
    setCurrentScreen('mos-selection');
  };

  // Handler for "Try Different MOS" - go back to MOS selection for fresh generation
  const handleTryDifferentMOS = () => {
    setUserData(prev => ({
      ...prev,
      selectedMOS: undefined,
      generatedImages: undefined,
    }));
    // Restart music for MOS selection
    startMusic(0.15);
    setCurrentScreen('mos-selection');
  };

  // Helper to preload images into browser cache
  const preloadImages = (urls: string[]): Promise<void[]> => {
    return Promise.all(
      urls.filter(Boolean).map(url =>
        new Promise<void>((resolve) => {
          const img = new Image();
          img.onload = () => resolve();
          img.onerror = () => resolve(); // Don't block on errors
          img.src = url;
        })
      )
    );
  };

  // Handler when each MOS (including priority) finishes generating.
  // Uploads to R2 and caches the result so "Try Different MOS" can reveal instantly.
  const handleMOSComplete = useCallback(async (mosId: string, images: GeneratedImages) => {
    console.log(`MOS ${mosId} complete, uploading to R2...`);
    try {
      const result = await uploadImages(images.portrait, images.field, mosId);
      // Prefer R2 URLs; fall back to the base64 from Gemini if R2 failed
      setAllMOSImages(prev => ({
        ...prev,
        [mosId]: {
          portrait: result.portraitUrl || images.portrait,
          field: result.fieldUrl || images.field,
        },
      }));
    } catch (error) {
      console.error(`Failed to upload MOS ${mosId} to R2:`, error);
    }
  }, [uploadImages]);

  // Handler when ALL MOS have finished generating (after the user is already on reveal)
  const handleAllMOSComplete = useCallback((allImages: AllMOSImages) => {
    console.log('All MOS generation complete:', Object.keys(allImages));
  }, []);

  const handleProcessingComplete = useCallback(async (generatedImages: GeneratedImages) => {
    // Prevent multiple calls (React effect can fire multiple times)
    if (processingCompleteCalledRef.current) {
      console.log('🎯 handleProcessingComplete already called, skipping');
      return;
    }
    processingCompleteCalledRef.current = true;

    console.log('🎯 handleProcessingComplete called!', {
      hasPortrait: !!generatedImages.portrait,
      hasField: !!generatedImages.field,
      portraitLength: generatedImages.portrait?.length,
      fieldLength: generatedImages.field?.length,
    });
    // Upload images to R2 for permanent storage (hides Replicate URLs from client)
    if (generatedImages.portrait && generatedImages.field) {
      const priorityMosId = userData.selectedMOS?.id;
      console.log('🎯 Starting upload to R2...', { priorityMosId });
      const result = await uploadImages(generatedImages.portrait, generatedImages.field, priorityMosId);
      console.log('🎯 Upload result:', result);

      if (result.success && result.portraitUrl && result.fieldUrl) {
        // Preload R2 URLs before showing RevealScreen (only R2 URLs reach the browser)
        console.log('🎯 Preloading images...');
        await preloadImages([result.portraitUrl, result.fieldUrl]);
        console.log('🎯 Preload complete, transitioning to reveal...');

        const r2Images = {
          portrait: result.portraitUrl!,
          field: result.fieldUrl!,
        };

        setUserData(prev => ({
          ...prev,
          generatedImages: r2Images,
        }));

        // Seed the cache with priority MOS so re-selecting it hits the cache
        // even if handleMOSComplete's upload is still in-flight
        if (priorityMosId) {
          setAllMOSImages(prev => ({ ...prev, [priorityMosId]: r2Images }));
        }
      } else {
        // R2 failed - use R2 URLs if available, otherwise fall back to base64 from Gemini
        console.error('🎯 R2 upload failed, falling back to base64 images', result);
        const fallbackImages = {
          portrait: result.portraitUrl || generatedImages.portrait,
          field: result.fieldUrl || generatedImages.field,
        };
        setUserData(prev => ({
          ...prev,
          generatedImages: fallbackImages,
        }));
        if (priorityMosId) {
          setAllMOSImages(prev => ({ ...prev, [priorityMosId]: fallbackImages }));
        }
      }
    } else {
      console.log('🎯 No images to upload, using generated images directly');
      setUserData(prev => ({ ...prev, generatedImages }));
    }
    console.log('🎯 Setting screen to reveal!');
    setCurrentScreen('reveal');
  }, [userData.selectedMOS?.id, uploadImages]);

  const handleRestart = () => {
    // Preserve lastName so it's pre-filled on restart
    const preservedLastName = userData.lastName;
    setUserData({ lastName: preservedLastName });
    setAllMOSImages(null);
    resetSession();
    setCurrentScreen('attract');
  };

  // Start completely fresh session - clears everything and returns to attract screen
  const handleStartNewSession = () => {
    stopMusic();
    setUserData({});
    setAllMOSImages(null);
    resetSession();
    setCurrentScreen('attract');
  };

  const handleGenerationError = () => {
    // Clear captured image and go back to photo capture for a new photo
    // Session remains intact so user doesn't lose their other selections
    processingCompleteCalledRef.current = false;
    setUserData(prev => ({ ...prev, capturedImage: undefined }));
    setCurrentScreen('photo-capture');
  };

  return (
    <div className="h-screen w-screen bg-army-black overflow-hidden relative">

      {/* Screen router with animations */}
      <AnimatePresence mode="wait">
        {currentScreen === 'attract' && (
          <AttractScreen key="attract" onStart={handleStart} />
        )}

        {currentScreen === 'name-entry' && (
          <NameEntry
            key="name-entry"
            onSubmit={handleNameEntry}
            onBack={handleBackToAttract}
            initialLastName={userData.lastName}
          />
        )}

        {currentScreen === 'biodata' && (
          <BiodataEntry
            key="biodata"
            onSelect={handleSexSelection}
            onBack={handleBackToNameEntry}
            onClickSound={() => playSound('mosSelection')}
          />
        )}

        {currentScreen === 'mos-selection' && (
          <MOSSelection
            key="mos"
            onSelect={handleMOSSelection}
            onBack={handleBackToBiodata}
            onCardClick={() => {
              playSound('cardSwipe');
            }}
            onModalSelect={() => {
              playSound('mosSelection');
            }}
          />
        )}

        {currentScreen === 'photo-capture' && (
          <PhotoCapture key="photo" onCapture={handlePhotoCapture} onBack={handleBackToMOS} />
        )}

        {currentScreen === 'processing' && (
          <ProcessingScreen
            key="processing"
            userData={userData}
            onComplete={handleProcessingComplete}
            onMOSComplete={handleMOSComplete}
            onAllMOSComplete={handleAllMOSComplete}
            onError={handleGenerationError}
          />
        )}

        {currentScreen === 'reveal' && (
          <RevealScreen
            key="reveal"
            userData={userData}
            sessionId={session.sessionId}
            sessionCode={session.sessionCode}
            onRestart={handleRestart}
            onTryDifferentMOS={handleTryDifferentMOS}
            onStartNewSession={handleStartNewSession}
          />
        )}
      </AnimatePresence>

      {/* HUD Status Bar */}
      <div className="fixed top-0 left-0 right-0 h-12 4kl:h-20 4k:h-32 bg-army-black/80 border-b-2 4kl:border-b-3 4k:border-b-6 border-army-gold/30 flex items-center justify-between px-6 4kl:px-12 4k:px-16 z-50">
        <div className="flex items-center space-x-4 4kl:space-x-8 4k:space-x-12 text-sm 4kl:text-xl 4k:text-4xl text-army-gold font-hud">
          <span>SYS: ONLINE</span>
          <span className="text-hud-green text-lg 4kl:text-3xl 4k:text-6xl">●</span>
          <span>FPS: 60</span>
        </div>
        <div className="flex items-center text-sm 4kl:text-xl 4k:text-4xl font-hud uppercase">
          <span className="text-white">MOS.AIC</span>
        </div>
        <div className="flex items-center space-x-4 4kl:space-x-8 4k:space-x-12 text-sm 4kl:text-xl 4k:text-4xl text-army-gold font-hud">
          <span>v2.0.1</span>
          <span>AI: ACTIVE</span>
          <span className="text-hud-green animate-pulse text-lg 4kl:text-3xl 4k:text-6xl">●</span>
        </div>
      </div>

      {/* Session Timeout Modal */}
      <TimeoutModal
        isOpen={showWarning}
        countdownSeconds={WARNING_SECONDS}
        onStayActive={handleStayActive}
        onTimeout={handleWarningTimeout}
      />
    </div>
  );
}

// Result page wrapper to extract sessionId from URL
function ResultPageWrapper() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('id') || '';

  return <ResultPage sessionId={sessionId} />;
}

// Main App with routing
function App() {
  return (
    <BrowserRouter>
      <SessionProvider>
        <Routes>
          <Route path="/" element={<MainExperience />} />
          <Route path="/result" element={<ResultPageWrapper />} />
          <Route path="/dashboard" element={<Dashboard />} />
          {/* Fallback for unknown routes */}
        </Routes>
      </SessionProvider>
    </BrowserRouter>
  );
}

export default App;
